import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation, t } from '../lib/i18n';

const AboutPage: React.FC = () => {
  const { language } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t("about.pageTitle", "About Us & Leadership | CRE-Dominion Commercial Real Estate Intelligence");
  }, [language]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white px-4 sm:px-6 py-2 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center no-underline">
            <img 
              src={logo} 
              alt="CRE-Dominion Logo" 
              className="h-16 sm:h-20 md:h-24 w-auto object-contain mr-4 hover:opacity-95 transition-opacity" 
            />
          </Link>
          <div className="flex items-center gap-3 sm:gap-6 text-sm font-semibold">
            <Link to="/" className="text-slate-600 hover:text-blue-600 transition-colors no-underline hidden sm:inline">
              {t("about.navHome", "Home")}
            </Link>
            <Link to="/blog" className="text-slate-600 hover:text-blue-600 transition-colors no-underline hidden sm:inline">
              {t("about.navResearch", "Research")}
            </Link>
            <Link to="/contact" className="text-slate-600 hover:text-blue-600 transition-colors no-underline hidden sm:inline">
              {t("about.navContact", "Contact")}
            </Link>
            <LanguageSwitcher compact />
            <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-slate-900 hidden sm:inline-block">
              {t("about.navSignIn", "Sign In")}
            </Link>
            <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2 rounded-lg transition shadow-sm whitespace-nowrap">
              {t("about.navPilot", "Request Pilot")}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full space-y-12">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 px-3.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {t("about.heroBadge", "Institutional Commercial Real Estate Intelligence")}
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-900 mb-4">
            {t("about.heroTitle", "Architected for Institutional Asset Managers & Brokers")}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 font-medium leading-relaxed">
            {t("about.heroSubtitle", "CRE-Dominion automates commercial lease abstraction, CAM reconciliation, and algorithmic deal underwriting. We eliminate manual underwriting friction so real estate operators close deals with empirical precision.")}
          </p>
        </div>

        {/* Mission & Technology Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("about.pillar1Title", "Automated Lease Abstraction")}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t("about.pillar1Desc", "Extract rent schedules, escalation clauses, options, and critical expiration milestones from 80+ page commercial leases in seconds.")}
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("about.pillar2Title", "Predictive Underwriting")}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t("about.pillar2Desc", "Algorithmic valuation engine dynamically stress-tests cap rates, NOI trajectories, and debt service coverage ratios against live market comps.")}
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("about.pillar3Title", "Bank-Grade Confidentiality")}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t("about.pillar3Desc", "Strict multi-tenant cryptographic isolation. Your proprietary rent rolls, tenant contracts, and pro formas are never used for public training.")}
            </p>
          </div>
        </div>

        {/* About the Founder */}
        <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
            <div>
              <span className="inline-block bg-blue-50 text-blue-700 text-xs font-mono font-bold px-2.5 py-0.5 rounded border border-blue-200 uppercase tracking-wider mb-2">
                {t("about.founderLeadership", "Executive Leadership")}
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900">{t("about.founderName", "Ghazwan Alemara")}</h2>
              <p className="text-sm sm:text-base font-semibold text-slate-600 mt-1">
                {t("about.founderRole", "Founder & Lead Systems Architect · CRE-Dominion")} <span className="text-slate-400">({t("about.founderSub", "backed by Data Genius")})</span>
              </p>
            </div>
            <a
              href="https://www.linkedin.com/in/ghazwan-alemara/"
              target="_blank"
              rel="noopener noreferrer"
              className="self-start sm:self-center"
            >
              <button className="bg-[#0077B5] hover:bg-[#005f93] text-white font-bold text-xs px-4 py-2.5 rounded-lg shadow-sm transition-all inline-flex items-center gap-2 cursor-pointer">
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
                <span>{t("about.founderLinkedIn", "Connect on LinkedIn")}</span>
                <svg className="w-3.5 h-3.5 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            </a>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
            <p className="font-medium">
              {t("about.founderBio1", "Ghazwan Alemara is the Founder and Lead Systems Architect of CRE-Dominion, backed by the applied software research studio Data Genius. With a background specializing in enterprise systems architecture and quantitative software engineering, Ghazwan designed CRE-Dominion to solve the multi-billion dollar information asymmetry in commercial property lease management and underwriting.")}
            </p>
            <p className="font-medium">
              {t("about.founderBio2", "Holding an M.Sc. in Systems Sciences from Stockholm University (EQF Level 7), he applies deterministic computational models to replace error-prone manual spreadsheets with high-throughput algorithmic deal analysis and automated audit verification.")}
            </p>
          </div>

          {/* Credential Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 pt-6 mt-6 border-t border-slate-100">
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-wider">{t("about.card1Label", "Education")}</div>
              <div className="font-bold text-sm text-slate-900 mt-1">{t("about.card1Val", "M.Sc. Systems Sciences")}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("about.card1Sub", "Stockholm University (EQF 7)")}</div>
            </div>
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-wider">{t("about.card2Label", "Architecture")}</div>
              <div className="font-bold text-sm text-slate-900 mt-1">{t("about.card2Val", "Lead Systems Architect")}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("about.card2Sub", "CRE Algorithmic Intelligence")}</div>
            </div>
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-wider">{t("about.card3Label", "Engineering Studio")}</div>
              <div className="font-bold text-sm text-slate-900 mt-1">{t("about.card3Val", "Data Genius")}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("about.card3Sub", "Applied Systems R&D")}</div>
            </div>
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="text-[11px] font-mono font-bold text-slate-500 uppercase tracking-wider">{t("about.card4Label", "Data Defense")}</div>
              <div className="font-bold text-sm text-slate-900 mt-1">{t("about.card4Val", "Zero-Trust Isolation")}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t("about.card4Sub", "Institutional Privacy")}</div>
            </div>
          </div>
        </section>

        
        {/* Enterprise Security & Data Sovereignty Bento */}
        <section className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 shadow-sm space-y-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full mb-3">
              <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t("about.secBadge", "Bank-Grade Confidentiality & Data Sovereignty")}
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-3">
              {t("about.secTitle", "Institutional Security & Zero-Trust Privacy")}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              {t("about.secSub", "Engineered specifically for institutional asset managers, private equity funds, and commercial brokerages managing sensitive deal flow.")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs mb-3 font-mono">01</div>
              <h3 className="font-bold text-slate-900 text-base mb-1.5">{t("about.secCard1Title", "Zero Model Training")}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t("about.secCard1Desc")}</p>
            </div>
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs mb-3 font-mono">02</div>
              <h3 className="font-bold text-slate-900 text-base mb-1.5">{t("about.secCard2Title", "AES-256 & TLS 1.3 Cryptography")}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t("about.secCard2Desc")}</p>
            </div>
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs mb-3 font-mono">03</div>
              <h3 className="font-bold text-slate-900 text-base mb-1.5">{t("about.secCard3Title", "Strict Tenant Partitioning")}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t("about.secCard3Desc")}</p>
            </div>
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs mb-3 font-mono">04</div>
              <h3 className="font-bold text-slate-900 text-base mb-1.5">{t("about.secCard4Title", "SOC-2 & GDPR Ready Architecture")}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{t("about.secCard4Desc")}</p>
            </div>
          </div>
        </section>

        {/* 14-Day Institutional Pilot Framework */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 sm:p-10 shadow-sm space-y-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-900/60 text-blue-300 border border-blue-700 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full mb-3">
              <svg className="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {t("about.pilotBadge", "Institutional Risk Reversal")}
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-3">
              {t("about.pilotTitle", "The 14-Day Institutional Pilot Framework")}
            </h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              {t("about.pilotSub", "Evaluate CRE-Dominion risk-free on your active commercial acquisitions before committing to enterprise roll-out.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
              <div className="text-xs font-mono font-bold text-blue-400 mb-2">STAGE 01</div>
              <h3 className="font-bold text-white text-base mb-2">{t("about.pilotStep1Title", "1. Empirical Lease Benchmark")}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{t("about.pilotStep1Desc")}</p>
            </div>
            <div className="p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
              <div className="text-xs font-mono font-bold text-emerald-400 mb-2">STAGE 02</div>
              <h3 className="font-bold text-white text-base mb-2">{t("about.pilotStep2Title", "2. Custom Model Calibration")}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{t("about.pilotStep2Desc")}</p>
            </div>
            <div className="p-5 bg-slate-800/80 border border-slate-700 rounded-xl">
              <div className="text-xs font-mono font-bold text-purple-400 mb-2">STAGE 03</div>
              <h3 className="font-bold text-white text-base mb-2">{t("about.pilotStep3Title", "3. Direct Sovereign Engineering SLA")}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{t("about.pilotStep3Desc")}</p>
            </div>
          </div>
        </section>

        {/* Pilot CTA */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            {t("about.ctaTitle", "Deploy CRE-Dominion for Your Commercial Portfolio")}
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto text-sm sm:text-base mb-6 leading-relaxed">
            {t("about.ctaDesc", "Experience 60-second lease abstraction and institutional underwriting on your active deals with our enterprise pilot program.")}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm px-6 py-3 rounded-lg shadow-md transition-all inline-flex items-center gap-2 no-underline">
              <span>{t("about.ctaBtn", "Request Institutional Pilot")}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <Link to="/demo" className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm px-6 py-3 rounded-lg transition-all no-underline">
              {t("about.ctaDemo", "Explore 60-Second Demo")}
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white px-6 py-8 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} {t("about.footerRights", "CRE-Dominion · A Data Genius Venture. All rights reserved.")}</p>
          <div className="flex gap-4">
            <Link to="/" className="text-slate-500 hover:text-slate-900 no-underline">{t("about.navHome", "Home")}</Link>
            <Link to="/about" className="text-slate-500 hover:text-slate-900 no-underline font-bold text-slate-800">About</Link>
            <Link to="/blog" className="text-slate-500 hover:text-slate-900 no-underline">{t("about.navResearch", "Research")}</Link>
            <Link to="/contact" className="text-slate-500 hover:text-slate-900 no-underline">{t("about.navContact", "Contact")}</Link>
            <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-900 no-underline">Privacy</Link>
            <Link to="/terms-of-service" className="text-slate-500 hover:text-slate-900 no-underline">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
